import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import GoogleMapsStaticMap from 'components/google-maps/GoogleMapsStaticMap';

const UNKNOWN = 'desconocido';

function getVerificationCaption({
  isVerified,
  isVerifiedBy,
  isVerifiedAt,
  isPartialMatch,
}) {
  if (isVerified) {
    const who = isVerifiedBy || UNKNOWN;
    const when = isVerifiedAt ? ` el ${isVerifiedAt}` : '';
    return `Verificada manualmente por ${who}${when}`;
  }
  if (isPartialMatch) {
    return 'Coincidencia parcial — la dirección puede tener errores';
  }
  return 'Verificada automáticamente por el sistema';
}

function OrderAddressMap({
  computedAddressLabel,
  sourceAddress,
  reference,
  coordinates,
  isVerified,
  isVerifiedBy,
  isVerifiedAt,
  isPartialMatch,
}) {
  const hasCoordinates =
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    Number.isFinite(coordinates[0]) &&
    Number.isFinite(coordinates[1]);

  // GeoJSON coordinates are [lng, lat]; Google Maps URLs expect "lat,lng".
  const googleMapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates[1]},${coordinates[0]}`
    : null;

  const verificationCaption = getVerificationCaption({
    isVerified,
    isVerifiedBy,
    isVerifiedAt,
    isPartialMatch,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Dirección procesada
        </Typography>
        <Typography variant="body2">
          {computedAddressLabel || 'Sin dirección procesada'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Dirección original (ecommerce)
        </Typography>
        <Typography variant="body2">
          {sourceAddress || 'Sin dirección original'}
        </Typography>
      </Box>
      {reference && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Referencia ingresada por el usuario:
          </Typography>
          <Typography variant="body2">{reference}</Typography>
        </Box>
      )}
      <Box>
        <GoogleMapsStaticMap coordinates={coordinates} />
        {hasCoordinates && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
          >
            {`Lat ${coordinates[1].toFixed(6)} · Lng ${coordinates[0].toFixed(
              6,
            )}`}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        color={isPartialMatch ? 'error' : 'text.secondary'}
      >
        {verificationCaption}
      </Typography>
      {googleMapsUrl && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewOutlinedIcon />}
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ alignSelf: 'flex-start' }}
        >
          Abrir en Google Maps
        </Button>
      )}
    </Box>
  );
}

OrderAddressMap.propTypes = {
  computedAddressLabel: PropTypes.string,
  sourceAddress: PropTypes.string,
  reference: PropTypes.string,
  coordinates: PropTypes.arrayOf(PropTypes.number),
  isVerified: PropTypes.bool,
  isVerifiedBy: PropTypes.string,
  isVerifiedAt: PropTypes.string,
  isPartialMatch: PropTypes.bool,
};

OrderAddressMap.defaultProps = {
  computedAddressLabel: '',
  sourceAddress: '',
  reference: '',
  coordinates: null,
  isVerified: false,
  isVerifiedBy: '',
  isVerifiedAt: '',
  isPartialMatch: false,
};

export default OrderAddressMap;
